/**
 * Opening a Pathways file.
 *
 * Two writers put explorations in a file, and they write different shapes:
 *
 * - The export sheet's JSON row writes the bare `Session` — the graph, its ops,
 *   ledger, runs and research workspace — with no storage envelope, so no
 *   schema version, no save clock and no reading (lens, temper, selection).
 * - The durability notice's recovery copy writes
 *   `{ format: 'pathways-recovery', version: 1, documents }`, where each
 *   document is a whole `StoredSession` exactly as `storedSessionJson` wrote it:
 *   the open exploration and every outgoing one whose save had not landed.
 *
 * Both are read through `readStoredSessionJson`, the one untrusted-input codec
 * that storage and share links use — which is what re-gates a stored refutation
 * (`refutedIsEarned`, in `asVerification`) and keeps every model stamp as
 * written. There is no file-specific interpretation of any field. A lone
 * `StoredSession`, the shape a share link carries, reads as itself.
 *
 * The bare session is wrapped in an envelope at this build's `SCHEMA`, because
 * it names no version of its own. Every migration since v4 is an identity step,
 * so an export from any build that had runs reads whole; one from before that
 * loses what a migration would have lifted, and loses it the codec's way —
 * dropped, never re-attributed. One written by a NEWER build is read by this
 * one's codec and loses what it does not know; the file itself is untouched.
 *
 * Nothing here mints an id or writes anything. `openExplorationFile` in
 * `autosave.ts` installs what this returns, as copies.
 */

import { SCHEMA, readStoredSessionJson, type StoredSession } from './persist'
import type { Session } from './types'

/**
 * The export sheet's JSON row: the session itself, field for field, indented for
 * a person or a tool. Beside its reader so the two cannot drift apart.
 */
export const explorationJson = (session: Session): string => JSON.stringify(session, null, 2)

/** The recovery copy's own marker, written by `recoveryDocumentsJson`. */
export const RECOVERY_FORMAT = 'pathways-recovery'
const RECOVERY_VERSION = 1

/**
 * `ok` means at least one exploration read. `unreadable` counts a recovery
 * copy's documents that did not, so a partial file says so instead of shrinking.
 */
export type ExplorationFileRead =
  { kind: 'ok'; docs: StoredSession[]; unreadable: number } | { kind: 'invalid' }

const object = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

/** Read a file's text. Never throws on file input. */
export function readExplorationFile(text: string): ExplorationFileRead {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return { kind: 'invalid' }
  }
  const root = object(parsed)
  if (!root) return { kind: 'invalid' }

  if (root.format === RECOVERY_FORMAT) {
    // A later recovery layout is refused whole rather than guessed at, the same
    // answer the codec gives a document from a newer schema.
    if (root.version !== RECOVERY_VERSION || !Array.isArray(root.documents)) {
      return { kind: 'invalid' }
    }
    const docs: StoredSession[] = []
    let unreadable = 0
    for (const row of root.documents) {
      const doc = readStoredSessionJson(JSON.stringify(row))
      if (doc) docs.push(doc)
      else unreadable += 1
    }
    return docs.length ? { kind: 'ok', docs, unreadable } : { kind: 'invalid' }
  }

  // A stored document — what storage holds and a share link carries.
  if (typeof root.v === 'number') {
    const doc = readStoredSessionJson(text)
    return doc ? { kind: 'ok', docs: [doc], unreadable: 0 } : { kind: 'invalid' }
  }

  // The export sheet's JSON: a bare session. Wrapped, not interpreted — the
  // codec decides what survives, and the reading falls to its defaults.
  if (object(root.nodes) && Array.isArray(root.order)) {
    const doc = readStoredSessionJson(JSON.stringify({ v: SCHEMA, session: root, ui: {} }))
    return doc ? { kind: 'ok', docs: [doc], unreadable: 0 } : { kind: 'invalid' }
  }
  return { kind: 'invalid' }
}
